import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { cn } from "@excellent/ui"

interface Props {
  content: string
  className?: string
}

/**
 * Renders Claude assistant text as GitHub-flavored markdown with app-consistent
 * styling. Cross-product block: shared by the Lens session replay and the
 * Copilot/Chat rail. Lives at the top level of blocks/ (not under any product)
 * because it carries no product-specific concerns.
 */
export function AssistantMarkdown({ content, className }: Props) {
  return (
    <div className={cn('assistant-md type-body text-foreground', className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => (
            <h1 className="mt-4 mb-2 border-b border-border pb-1 type-heading first:mt-0">{children}</h1>
          ),
          h2: ({ children }) => (
            <h2 className="mt-4 mb-2 type-title  first:mt-0">{children}</h2>
          ),
          h3: ({ children }) => (
            <h3 className="mt-3 mb-1.5 type-title  first:mt-0">{children}</h3>
          ),
          h4: ({ children }) => (
            <h4 className="mt-2 mb-1 type-title  first:mt-0">{children}</h4>
          ),
          p: ({ children }) => <p className="my-2 leading-body first:mt-0 last:mb-0">{children}</p>,
          ul: ({ children }) => (
            <ul className="my-2 list-disc space-y-1 pl-5 marker:text-foreground">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="my-2 list-decimal space-y-1 pl-5 marker:text-foreground">{children}</ol>
          ),
          li: ({ children }) => <li className="leading-body [&>p]:my-0">{children}</li>,
          blockquote: ({ children }) => (
            <blockquote className="my-2 border-l border-primary/40 pl-3 text-foreground">{children}</blockquote>
          ),
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className=" text-foreground underline decoration-primary/40 underline-offset-2 hover:decoration-primary"
            >
              {children}
            </a>
          ),
          strong: ({ children }) => <strong className=" text-foreground">{children}</strong>, // design-audit-ignore html-strong-bold: markdown renderer must emit semantic <strong>
          em: ({ children }) => <em className="italic text-foreground">{children}</em>,
          hr: () => <hr className="my-4 border-border" />,
          table: ({ children }) => (
            <div className="my-3 overflow-x-auto rounded-md surface-edge">
              <table className="w-full min-w-[16rem] border-collapse type-body">{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead className="bg-muted/50">{children}</thead>,
          th: ({ children }) => (
            <th className="border-b border-border px-2 py-1.5 text-left ">{children}</th>
          ),
          td: ({ children }) => <td className="border-b border-border/80 px-2 py-1.5 align-top">{children}</td>,
          tr: ({ children }) => <tr className="border-border/60">{children}</tr>,
          code: ({ className, children }) => {
            const isBlock = /language-/.test(className ?? '')
            if (isBlock) {
              return (
                <code className={cn('font-mono type-body leading-body text-foreground', className)}>
                  {children}
                </code>
              )
            }
            return (
              <code className="rounded bg-muted px-1.5 py-0.5 font-mono type-body text-foreground before:content-none after:content-none">
                {children}
              </code>
            )
          },
          pre: ({ children }) => (
            <pre className="my-3 overflow-x-auto rounded-lg surface-edge bg-muted/60 p-3 font-mono type-body leading-body [&>code]:bg-transparent [&>code]:p-0">
              {children}
            </pre>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
}
